/**
 * How old this section's data is — the one thing about a source a pilot has to
 * see, in the one place they will look for it: the section's own header.
 *
 * Three states, one slot, never more than one line:
 *
 *   · **read** — „aktualisiert 11:31Z", or „vor 4 min" while it is younger than
 *     an hour, because a relative age is read faster than a clock time and a
 *     clock time is read faster than „vor 143 min"
 *   · **reading** — a turning ⟳ in the same slot, no text that moves the layout
 *   · **failed** — the age AND one sentence that says what happens next
 *     („vor 12 min · Wetter gerade nicht erreichbar – wird erneut versucht.").
 *     The retry is already scheduled and there is nothing here for the pilot to
 *     do — but the values are still on the form and still ageing, so the age is
 *     the half that must NOT disappear when a source goes down
 *
 * The Wetter chip carries a second time: the OLDEST observation on the form
 * („gerade aktualisiert · METAR 10:50Z"). They are different questions — the
 * ten-minute beat re-reads an unchanged METAR and moves the fetch time while the
 * sky it describes is an hour old — and it is the OBSERVATION that ages the chip
 * (neutral, amber at an hour, red at ninety minutes; the age is always printed
 * beside the colour, so colour is never the only carrier).
 *
 * Beside it exactly two controls, both icons: ⟳ to fetch now, and ⓘ for the two
 * sentences that say where the numbers come from. Everything else a source could
 * say about itself — model names, endpoints, licence codes, the hour a forecast
 * was interpolated for — is behind that ⓘ and on the sources page, never in the
 * pilot's way.
 *
 * One thing is not visible at all: since this pass removed the buttons, data
 * arrives unasked, so each ARRIVAL is announced once through a polite live
 * region. Once — not on every tick of the relative age, which would make the
 * chip chatter.
 */
import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';

import { IconRefresh } from '../app/icons';
import { ageLevel, ageOf, type AgeLimits } from '../autoload';
import { useFamily, type Family } from '../freshness';
import { useT } from '../i18n';
import { useTerrainStatus, useWindsStatus, useWxStatus } from '../planContext';
import { zTime } from '../wx';
import { InfoPopover, type InfoLink } from './InfoPopover';

/** Where each family's ⓘ links to. Proper nouns, so they are not in the catalogue. */
const LINKS: Record<Family, readonly InfoLink[]> = {
  wx: [{ href: 'https://aviationweather.gov/', text: 'aviationweather.gov' }],
  winds: [{ href: 'https://open-meteo.com/', text: 'open-meteo.com' }],
  terrain: [
    { href: 'https://mapterhorn.com/attribution', text: 'mapterhorn.com' },
    { href: 'https://www.openaip.net/', text: 'openaip.net' },
  ],
  geo: [{ href: 'https://www.openaip.net/', text: 'openaip.net' }],
};

/** A relative age has to keep up with the clock; half a minute is close enough. */
const TICK_MS = 30_000;

function useTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * The two times this chip may show, and the plan's own memory of them.
 *
 * `stored` is what the DOCUMENT remembers, for a tab that has not fetched yet.
 * `observed` is the wx family's second clock and the one that matters most: when
 * the OLDEST bulletin on the form was actually measured. They are not the same
 * number — a ten-minute beat that re-reads an unchanged METAR moves the fetch
 * time and not the observation, so a chip that showed only the fetch could say
 * „gerade aktualisiert" over an hour-old sky.
 */
function useSourceTimes(family: Family): { stored: number | null; observed: number | null } {
  const wx = useWxStatus();
  const winds = useWindsStatus();
  const terrain = useTerrainStatus();
  const raw =
    family === 'wx' ? wx?.fetched_at : family === 'winds' ? winds?.fetched_at : family === 'terrain' ? terrain?.fetched_at : null;
  const ms = raw ? Date.parse(raw) : NaN;
  let observed: number | null = null;
  if (family === 'wx') {
    // the oldest, not the newest: what the pilot needs to know is how stale the
    // stalest report they are reading is
    for (const st of wx?.stations ?? []) {
      const at = st.obs_time ? Date.parse(st.obs_time) : NaN;
      if (Number.isFinite(at) && (observed === null || at < observed)) observed = at;
    }
  }
  return { stored: Number.isFinite(ms) ? ms : null, observed };
}

/**
 * How old is too old, per source.
 *
 * A METAR is issued hourly, so an hour is where one stops being current and
 * ninety minutes is where it should not be planned on. A wind forecast ages far
 * more slowly and a terrain profile does not age at all — the ground does not
 * move, and what openAIP publishes about an aerodrome changes on the AIRAC
 * cycle. Colour is never the only carrier: the age itself is printed beside it.
 */
const LIMITS: Record<Family, AgeLimits | null> = {
  wx: { warn: 60 * 60_000, bad: 90 * 60_000 },
  winds: { warn: 3 * 3600_000, bad: 6 * 3600_000 },
  terrain: null,
  geo: null,
};

export function Freshness({
  family,
  action = true,
  label = true,
  info,
}: {
  family: Family;
  /** false where the ⟳ lives elsewhere — one source has exactly one refresh */
  action?: boolean;
  /** false where the section's own heading already names the source */
  label?: boolean;
  /** one more line for the ⓘ — the published limits of THIS answer */
  info?: string | undefined;
}) {
  const t = useT();
  const state = useFamily(family);
  const { stored, observed } = useSourceTimes(family);
  const at = state?.at ?? stored;
  const now = useTick(at !== null && !state?.busy);

  // nothing has been read and nothing is being read: an empty section says so by
  // being empty
  if (!state && at === null) return null;

  const busy = state?.busy === true;
  const failed = state?.error !== null && state?.error !== undefined;
  const age = ageOf(at, now);
  const zAt = (ms: number) => zTime(new Date(ms).toISOString());

  const ageText =
    age.kind === 'now'
      ? t('fresh.now')
      : age.kind === 'min'
        ? t('fresh.ago', { n: age.mins })
        : age.kind === 'time'
          ? t('fresh.at', { time: zAt(at ?? 0) })
          : '';

  /* When the weather itself was measured, beside when it was read. The fetch time
     alone answers the wrong question: it says how busy the app has been. */
  const parts = [ageText];
  if (observed !== null && !failed) parts.push(t('fresh.obs', { time: zAt(observed) }));
  /* A failure must NOT take the age off the screen — the values are still on the
     form and are still ageing, and that is the half a pilot needs. */
  if (failed) parts.push(t('fresh.err.' + family));
  const text = parts.filter(Boolean).join(' · ');

  /* The wx chip ages by its OBSERVATION, everything else by its fetch. */
  const level = failed ? 'bad' : ageLevel(family === 'wx' ? observed : at, now, LIMITS[family]);

  /* One polite announcement per ARRIVAL, and one per failure — never per tick.
     `at` moves only when a fetch succeeds, so „vor 4 min" turning into „vor 5 min"
     is silent while the data that arrived unasked is not. */
  const live = failed
    ? t('fresh.err.' + family)
    : busy || at === null
      ? ''
      : t('fresh.fam.' + family) + ': ' + t('fresh.at', { time: zAt(at) });

  /* the chip lives inside a <summary>: a click on it must not fold the section */
  const swallow = (e: ReactMouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <span className={'fresh' + (level ? ' ' + level : '')}>
      {/* the failure sentence names the source itself — „Wetter Wetter gerade nicht
          erreichbar" is the label saying it twice */}
      {label && !failed ? <span className="freshlab">{t('fresh.fam.' + family)}</span> : null}
      {busy ? <span className="freshspin" aria-hidden="true" /> : <span className="freshage">{text}</span>}
      {/* the data now arrives unasked, so its arrival is announced; the spinner is
          a picture and says nothing on its own */}
      <span className="vh" aria-live="polite">{busy ? t('fresh.busy') : live}</span>
      {action && state ? (
        <button
          type="button"
          className="freshbtn"
          title={t('fresh.refresh')}
          aria-label={t('fresh.refresh')}
          disabled={busy}
          onClick={e => {
            swallow(e);
            state.refresh();
          }}
        >
          <IconRefresh />
        </button>
      ) : null}
      <InfoPopover body={t('src.info.' + family)} links={LINKS[family]} {...(info ? { extra: info } : {})} />
    </span>
  );
}
